import {
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  FetchBaseQueryMeta,
  QueryReturnValue,
  fetchBaseQuery
} from '@reduxjs/toolkit/query/react';
import { RootState } from '../store/state';
import { clearAuthResponse, setNewToken } from '../modules/auth/state/authSlice';
import { api as sellBooksApi } from '../api';
import { toast } from 'react-toastify';

export type ErrorDataMessage = Record<string, string[]>;

export const isDev = process.env.NODE_ENV === 'development';

export const socketProtocol = isDev ? 'ws://' : 'wss://';
export const HOST_NAME = isDev ? 'localhost:8000' : 'server.sellbooks.ai';

const DEV_BASE_URL = `http://${HOST_NAME}/`;
const PROD_BASE_URL = `https://${HOST_NAME}/`;

export const BASE_URL = isDev ? DEV_BASE_URL : PROD_BASE_URL;

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: (headers, api) => {
    const { token } = (api.getState() as RootState).auth;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  }
});

export const baseQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const authInfo = localStorage.getItem('authInfo');
    if (!authInfo) {
      api.dispatch(clearAuthResponse());
      api.dispatch(sellBooksApi.util.resetApiState());
      window.location.href = '/login';
      return result;
    }
    const refreshToken = JSON.parse(authInfo).refresh;

    type QueryResult = QueryReturnValue<
      { access: string },
      FetchBaseQueryError,
      FetchBaseQueryMeta
    >;

    const refreshResult = (await baseQuery(
      {
        url: '/auth/token/refresh/',
        method: 'POST',
        body: { refresh: refreshToken }
      },
      api,
      extraOptions
    )) as QueryResult;

    if (refreshResult.data) {
      api.dispatch(setNewToken(refreshResult.data.access));
      result = await baseQuery(args, api, extraOptions);
    } else {
      api.dispatch(clearAuthResponse());
      api.dispatch(sellBooksApi.util.resetApiState());
      window.location.href = '/login';
    }
  }

  if (result.error && result.error.status === 400) {
    const errorData = result.error.data as ErrorDataMessage;
    if (errorData?.non_field_errors) {
      toast(errorData.non_field_errors?.[0], { type: 'error' });
    }
  }
  return result;
};
